import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { catchError, Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PaginationOptions } from '../models/common/pagination-options.model';
import { BenefitBooking, PaginatedBenefits } from '../models/benefits/paginated-benefits.model';
import { CurrentBenefits } from '../models/benefits/current-benefits';
import { Provider } from '../models/scheduling/services-scheduling.model';
import { ClaimWash } from '../models/benefits/claim-wash.model';
import { ResponseClaim } from '../models/benefits/response-claim.model';
import { CurrentDiscounts } from '../models/benefits/current-discounts.model';
import { ClaimDiscount } from '../models/benefits/claim-discount.model';
import { ResponseClaimDiscount } from '../models/benefits/response-claim-discount.model';

@Injectable({ providedIn: 'root' })
export class BenefitsService {
    private readonly baseUrl = environment.apiUrl;
    private readonly transactionsUrl = `${this.baseUrl}corporate/`;

    readonly listBenefits = signal<BenefitBooking[] | null>(null);
    readonly totalBenefits = signal<number | null>(null);

    readonly currentPage = signal<number | null>(null);
    readonly pageSize = signal<number | null>(null);
    readonly totalPages = signal<number | null>(null);

    readonly currentBenefits = signal<CurrentBenefits | null>(null);

    readonly listCarWashProviders = signal<Provider[] | null>(null);

    readonly listDiscounts = signal<CurrentDiscounts | null>(null);

    constructor(private http: HttpClient) { }

    private getBenefitsPaginated(options: PaginationOptions): Observable<PaginatedBenefits> {
        let params = new HttpParams()
            .set('page', options.page)
            .set('size', options.size);

        if (options.search) params = params.set('search', options.search);
        if (options.sortBy) params = params.set('sortBy', options.sortBy);
        if (options.sortDir) params = params.set('sortDir', options.sortDir);

        if (options.filters) {
            Object.entries(options.filters).forEach(([key, value]) => {
                params = params.set(key, value);
            });
        }

        return this.http.get<PaginatedBenefits>(
            `${this.transactionsUrl}benefits/bookings`,
            { params, withCredentials: true }
        );
    }

    private getBenefitsServices(): Observable<CurrentBenefits> {
        return this.http.get<CurrentBenefits>(
            `${this.transactionsUrl}subscription/benefits`,
            { withCredentials: true }
        );
    }

    private getListCarWashProviders(): Observable<{ providers: Provider[] }> {
        return this.http.get<{ providers: Provider[] }>(
            `${this.transactionsUrl}car-wash-providers`,
            { withCredentials: true }
        );
    }

    private getListDiscounts(): Observable<CurrentDiscounts> {
        return this.http.get<CurrentDiscounts>(
            `${this.transactionsUrl}subscription/discounts`,
            { withCredentials: true }
        );
    }

    claimBenefit(data: ClaimWash, vehicleId: string, benefitId: string): Observable<ResponseClaim> {
        return this.http.post<ResponseClaim>(
            `${this.transactionsUrl}vehicles/${vehicleId}/benefits/${benefitId}/bookings`,
            data,
            { withCredentials: true }
        );
    }

    claimDiscount(data: ClaimDiscount, vehicleId: string): Observable<ResponseClaimDiscount> {
        return this.http.post<ResponseClaimDiscount>(
            `${this.transactionsUrl}vehicles/${vehicleId}/services/discount-booking`,
            data,
            { withCredentials: true }
        );
    }

    loadBenefitsPaginated(options: PaginationOptions): Observable<PaginatedBenefits> {
        return this.getBenefitsPaginated(options).pipe(
            tap(data => {
                this.listBenefits.set(data.benefitsBookings);
                this.totalBenefits.set(data.totalElements);
                this.currentPage.set(data.currentPage);
                this.pageSize.set(data.pageSize);
                this.totalPages.set(data.totalPages);
            }),
            catchError(error => {
                console.error('Error loading paginated benefits:', error);
                this.listBenefits.set([]);
                return of({
                    benefitsBookings: [],
                    totalElements: 0,
                    totalPages: 0,
                    currentPage: options.page,
                    pageSize: options.size,
                    statistics: {
                        totalSavings: 0,
                        totalBenefits: 0,
                        byBenefitType: {}
                    }
                });
            })
        );
    }


    loadListBenefits(): Observable<CurrentBenefits | null> {
        return this.getBenefitsServices().pipe(
            tap(data => {
                this.currentBenefits.set(data);
            }),
            catchError(error => {
                console.error('Error loading benefits services:', error);
                this.currentBenefits.set(null);
                return of(null);
            })
        );
    }

    loadCarWashProviders(): Observable<{ providers: Provider[] } | null> {
        return this.getListCarWashProviders().pipe(
            tap(data => {
                this.listCarWashProviders.set(data.providers);
            }),
            catchError(error => {
                console.error('Error loading car wash providers:', error);
                this.listCarWashProviders.set([]);
                return of(null);
            })
        );
    }

    loadListDiscounts(): Observable<CurrentDiscounts | null> {
        return this.getListDiscounts().pipe(
            tap(data => {
                this.listDiscounts.set(data);
            }),
            catchError(error => {
                console.error('Error loading discounts:', error);
                this.listDiscounts.set(null);
                return of(null);
            })
        );
    }

}